import { createContext, useContext } from 'react'

/**
 * Where each frame of a clip scene reads its media from (#89).
 *
 * A render plays only the kept parts of its window, back to back. The server
 * resolves them (`clipSceneTiming.ts`) and sends `clipScene.segments`, each a
 * media-relative `{ mediaStart, duration }`. This module turns those into
 * frame-exact pieces, one `<Sequence>` each:
 *
 * ```
 *   output frame   0 ─────── 90 ┊ 90 ────────── 150 ┊ 150 ─────── 330
 *                  │ piece 0    ┊ piece 1           ┊ piece 2       │
 *   media frame    3000 ── 3090 ┊ 3300 ─────── 3360 ┊ 3420 ──── 3600
 *                     (100 s)        (110 s)             (114 s)
 *   volume         1 1 1 … 1  ½ ┊ ½ 1 … … … … 1  ½ ┊ ½ 1 … … ⅔ ⅓ 0
 * ```
 *
 * Without segments there is one piece starting at frame 0, and its
 * `trimBefore`/`trimAfter` are exactly what `ClipScene` computed before #89 —
 * so a scene from an old config, or one with nothing cut, renders unchanged.
 *
 * `ClipScene` provides the pieces through `MediaTimelineContext` so that
 * `useFrequencyData` can follow the cuts without the ten layouts that call it
 * having to pass anything new.
 */

export type MediaSegment = { mediaStart: number; duration: number }

export type TimelinePiece = {
  /** First output frame of the piece. */
  from: number
  durationInFrames: number
  /** Media frame the piece starts at — `<Audio trimBefore>`. */
  trimBefore: number
  /** `<Audio trimAfter>`. */
  trimAfter: number
}

/**
 * The volume on the last frame before a seam and the first frame after it.
 *
 * A hard cut between two pieces of speech can click. `<Audio volume>` is
 * evaluated once per frame, so the shortest ramp available inside the
 * composition is one frame each side (33 ms at 30 fps); Descript-style
 * microfades are a few samples, which Remotion cannot express. Halving rather
 * than muting keeps the seam from reading as a gap.
 */
export const SEAM_VOLUME = 0.5

export function buildMediaTimeline({
  mediaStart = 0,
  duration,
  segments,
  fps,
}: {
  mediaStart?: number | undefined
  duration: number
  segments?: readonly MediaSegment[] | undefined
  fps: number
}): TimelinePiece[] {
  if (!segments || segments.length === 0) {
    const trimBefore = Math.round(mediaStart * fps)
    const durationInFrames = Math.round(duration * fps)
    return [
      {
        from: 0,
        durationInFrames,
        trimBefore,
        trimAfter: trimBefore + durationInFrames,
      },
    ]
  }

  const pieces: TimelinePiece[] = []
  let outputSeconds = 0
  for (const segment of segments) {
    // Each edge rounded from the running total, not each duration on its own,
    // so the pieces tile the output with no frame gained or lost to rounding.
    const from = Math.round(outputSeconds * fps)
    outputSeconds += segment.duration
    const to = Math.round(outputSeconds * fps)
    const durationInFrames = to - from
    if (durationInFrames <= 0) continue

    const trimBefore = Math.round(segment.mediaStart * fps)
    pieces.push({
      from,
      durationInFrames,
      trimBefore,
      trimAfter: trimBefore + durationInFrames,
    })
  }
  return pieces
}

/** The piece that plays at `frame`: the last one starting at or before it. */
export function pieceIndexAt(
  pieces: readonly TimelinePiece[],
  frame: number,
): number {
  let index = 0
  for (let i = 0; i < pieces.length; i++) {
    if (pieces[i]!.from > frame) break
    index = i
  }
  return index
}

/**
 * The media frame the scene shows at output `frame`. For a single piece this
 * is `frame + round(mediaStart * fps)`, which is what `useFrequencyData`
 * computed before #89.
 */
export function mediaFrameAt(
  pieces: readonly TimelinePiece[],
  frame: number,
): number {
  const piece = pieces[pieceIndexAt(pieces, frame)]
  if (!piece) return frame
  return piece.trimBefore + (frame - piece.from)
}

/**
 * The volume of piece `index` at `localFrame` (frames since the piece began,
 * which is what `<Audio volume>` is called with inside a `<Sequence>`).
 * Lowered at internal seams, and faded to silence over the scene's last
 * frames. Never at the scene's start.
 */
export function seamVolume(
  pieces: readonly TimelinePiece[],
  index: number,
  localFrame: number,
): number {
  const piece = pieces[index]
  if (!piece) return 1

  // The scene's own end, for every render, cut or not. A window usually ends
  // on a word's tail, and stopping the waveform mid-signal pops audibly
  // (heard on a Lambda render ending on "…the CTO."). Ramping the last frames
  // down lands the final sample at zero.
  if (index === pieces.length - 1) {
    const framesLeft = piece.durationInFrames - 1 - localFrame
    if (framesLeft < END_FADE.length) return END_FADE[framesLeft]!
  }

  if (pieces.length <= 1) return 1
  if (index > 0 && localFrame <= 0) return SEAM_VOLUME
  if (index < pieces.length - 1 && localFrame >= piece.durationInFrames - 1)
    return SEAM_VOLUME
  return 1
}

/**
 * The scene's last frames, last first: the final frame is silent. Three frames
 * (100 ms at 30 fps) is the shortest ramp that reaches zero without a step a
 * listener hears as its own click.
 */
const END_FADE = [0, 1 / 3, 2 / 3] as const

/** The scene's pieces, or `null` outside a `ClipScene`. */
export const MediaTimelineContext = createContext<TimelinePiece[] | null>(null)

export function useMediaTimeline(): TimelinePiece[] | null {
  return useContext(MediaTimelineContext)
}
